import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Metric,
  Field,
  TextInput,
  TextArea,
  Select,
  formatCurrency,
} from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { Continuity } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { proposalApi } from "../../lib/api";
import { DemoModeBanner, LiveModeBadge } from "../../components/ProposalGrounded";

// Opportunity hub: the entry point for the full solicitation→submission journey.
// Lists solicitations (with real upload), workspaces (with create), and runs the
// proposal-to-contract handoff + acceptance gate — all opportunity-scoped.
export function PaiOpportunityDetail() {
  const { activeOrg } = useAuth();
  const { opportunityId = "" } = useParams();
  const orgId = activeOrg?.id ?? "";

  const oppState = useAsync(async () => proposalApi.opportunity(orgId, opportunityId), [orgId, opportunityId]);
  const solState = useAsync(async () => proposalApi.solicitations(orgId, opportunityId), [orgId, opportunityId]);
  const wsState = useAsync(async () => proposalApi.workspaces(orgId), [orgId]);
  const contState = useAsync(async () => {
    try {
      return await proposalApi.continuity(orgId, opportunityId);
    } catch (e) {
      // No baseline frozen yet is an expected 404 — surface as empty, not error.
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  }, [orgId, opportunityId]);

  const o = oppState.data?.data;
  const demo = oppState.data?.demoMode ?? false;
  const solicitations = solState.data?.data.solicitations ?? [];
  const workspaces = (wsState.data?.data.workspaces ?? []).filter((w) => w.opportunityId === opportunityId);

  // Upload form
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  async function upload() {
    if (!title.trim() || !text.trim()) return;
    setUploading(true);
    setUploadErr(null);
    try {
      await proposalApi.uploadSolicitation(orgId, {
        title: title.trim(),
        opportunityId,
        files: [
          {
            filename: `${title.trim().replace(/\s+/g, "-").toLowerCase()}.txt`,
            mimeType: "text/plain",
            content: text,
          },
        ],
      });
      setTitle("");
      setText("");
      solState.reload();
    } catch (e) {
      setUploadErr(e instanceof ApiError ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  // Workspace create
  const [wsTitle, setWsTitle] = useState("");
  const [wsSolicitation, setWsSolicitation] = useState("");
  const [creatingWs, setCreatingWs] = useState(false);
  const [wsErr, setWsErr] = useState<string | null>(null);

  async function createWorkspace() {
    if (!wsTitle.trim()) return;
    setCreatingWs(true);
    setWsErr(null);
    try {
      await proposalApi.createWorkspace(orgId, {
        opportunityId,
        title: wsTitle.trim(),
        ...(wsSolicitation ? { solicitationId: wsSolicitation } : {}),
      });
      setWsTitle("");
      wsState.reload();
    } catch (e) {
      setWsErr(e instanceof ApiError ? e.message : "Could not create workspace.");
    } finally {
      setCreatingWs(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{o?.title ?? "Opportunity"}</h1>
          <p>
            {o?.agency ?? ""} {o?.opportunityNumber ? `· ${o.opportunityNumber}` : ""}
          </p>
        </div>
        <div className="ods-row">
          <LiveModeBadge demoMode={demo} />
          <Link to="/studio/proposal-ai/opportunities">
            <Button size="sm" variant="secondary">
              All opportunities
            </Button>
          </Link>
        </div>
      </div>

      <DemoModeBanner show={demo} />
      {oppState.loading && <LoadingState label="Loading opportunity…" />}
      {oppState.error && <ErrorState description={oppState.error.message} onRetry={oppState.reload} />}

      {o && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={o.stage} label="Stage" />
            </div>
            <div className="metric-card">
              <Metric value={o.setAside ?? "None"} label="Set aside" />
            </div>
            <div className="metric-card">
              <Metric value={o.estimatedValue ? formatCurrency(o.estimatedValue, "usd") : "Not stated"} label="Est. value" />
            </div>
            <div className="metric-card">
              <Metric value={o.proposalDueDate ?? "Not set"} label="Proposal due" tone="var(--ods-color-warning)" />
            </div>
          </div>

          {/* ---- Solicitations ---- */}
          <Card>
            <CardHeader title="Upload a solicitation" />
            <CardBody>
              <div className="ods-stack">
                <Field label="Document title">
                  {(id) => (
                    <TextInput
                      id={id}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. RFP W912-25-R-0001"
                    />
                  )}
                </Field>
                <Field label="Solicitation text" help="Paste the solicitation text. It is uploaded and shredded into requirements.">
                  {(id) => (
                    <TextArea
                      id={id}
                      rows={6}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Section L: Instructions to Offerors…"
                    />
                  )}
                </Field>
                {uploadErr && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{uploadErr}</p>}
                <div>
                  <Button size="sm" onClick={upload} disabled={uploading || !title.trim() || !text.trim()}>
                    {uploading ? "Uploading…" : "Upload solicitation"}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={`Solicitations · ${solicitations.length}`} />
            <CardBody>
              {solState.loading && <LoadingState label="Loading solicitations…" />}
              {solState.error && <ErrorState description={solState.error.message} onRetry={solState.reload} />}
              {!solState.loading && solicitations.length === 0 && (
                <EmptyState title="No solicitations" description="Upload the solicitation document to begin." />
              )}
              {solicitations.length > 0 && (
                <div className="ods-stack">
                  {solicitations.map((s) => (
                    <Link
                      key={s.id}
                      to={`/studio/proposal-ai/solicitations/${s.id}`}
                      className="launch-card"
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <div className="ods-row" style={{ justifyContent: "space-between" }}>
                        <div style={{ fontWeight: 600 }}>{s.title}</div>
                        <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                          <Badge tone="neutral">{s.documentType}</Badge>
                          <Badge tone={s.processingStatus === "processed" ? "success" : "warning"}>
                            {s.processingStatus}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* ---- Workspaces ---- */}
          <Card>
            <CardHeader title="Create a proposal workspace" />
            <CardBody>
              <div className="ods-stack">
                <Field label="Workspace title">
                  {(id) => (
                    <TextInput
                      id={id}
                      value={wsTitle}
                      onChange={(e) => setWsTitle(e.target.value)}
                      placeholder="e.g. Volume I: Technical"
                    />
                  )}
                </Field>
                {solicitations.length > 0 && (
                  <Field label="Link solicitation (optional)">
                    {(id) => (
                      <Select id={id} value={wsSolicitation} onChange={(e) => setWsSolicitation(e.target.value)}>
                        <option value="">None</option>
                        {solicitations.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.title}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                )}
                {wsErr && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{wsErr}</p>}
                <div>
                  <Button size="sm" onClick={createWorkspace} disabled={creatingWs || !wsTitle.trim()}>
                    {creatingWs ? "Creating…" : "Create workspace"}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={`Workspaces · ${workspaces.length}`} />
            <CardBody>
              {wsState.loading && <LoadingState label="Loading workspaces…" />}
              {!wsState.loading && workspaces.length === 0 && (
                <EmptyState title="No workspaces" description="Create a workspace to author the proposal." />
              )}
              {workspaces.length > 0 && (
                <div className="ods-stack">
                  {workspaces.map((w) => (
                    <Link
                      key={w.id}
                      to={`/studio/proposal-ai/workspace/${w.id}`}
                      className="launch-card"
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <div className="ods-row" style={{ justifyContent: "space-between" }}>
                        <div style={{ fontWeight: 600 }}>{w.title}</div>
                        <Badge tone="info">{w.status}</Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* ---- Proposal to contract handoff / acceptance gate ---- */}
          <ContinuityCard
            orgId={orgId}
            opportunityId={opportunityId}
            continuity={contState.data?.data ?? null}
            loading={contState.loading}
            workspaces={workspaces.map((w) => ({ id: w.id, title: w.title }))}
            onChange={contState.reload}
          />
        </>
      )}
    </>
  );
}

function ContinuityCard({
  orgId,
  opportunityId,
  continuity,
  loading,
  workspaces,
  onChange,
}: {
  orgId: string;
  opportunityId: string;
  continuity: Continuity | null;
  loading: boolean;
  workspaces: { id: string; title: string }[];
  onChange: () => void;
}) {
  const [workspaceId, setWorkspaceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      onChange();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  }

  const status = continuity?.status;
  const gatePassed = status === "approved" || continuity?.humanApprovedAt != null;

  return (
    <Card>
      <CardHeader
        title="Proposal to contract handoff"
        action={
          continuity ? (
            <Badge tone={gatePassed ? "success" : "info"}>{status ?? "Not started"}</Badge>
          ) : undefined
        }
      />
      <CardBody>
        {loading && <LoadingState label="Loading continuity…" />}
        <div className="ods-stack">
          <p style={{ margin: 0, color: "var(--ods-color-muted)" }}>
            Freeze the approved proposal as a contract baseline, request a Contract AI draft, then run the acceptance
            gate (human approval). Handoff never runs on its own.
          </p>
          {!continuity && (
            <>
              <Field label="Workspace to freeze">
                {(id) => (
                  <Select id={id} value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}>
                    <option value="">Select a workspace…</option>
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.title}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <div>
                <Button
                  size="sm"
                  disabled={busy || !workspaceId}
                  onClick={() => run(() => proposalApi.freezeBaseline(orgId, opportunityId, { workspaceId }))}
                >
                  {busy ? "Freezing…" : "Freeze baseline"}
                </Button>
              </div>
            </>
          )}
          {continuity && (
            <div className="ods-stack">
              <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
                <Badge tone="neutral">baseline {continuity.baselineChecksum.slice(0, 12)}…</Badge>
                {continuity.contractDraftRef && <Badge tone="info">{continuity.contractDraftRef}</Badge>}
              </div>
              <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy || status === "draft_created" || gatePassed}
                  onClick={() => run(() => proposalApi.requestContractDraft(orgId, opportunityId))}
                >
                  Request contract draft
                </Button>
                <Button
                  size="sm"
                  disabled={busy || gatePassed}
                  onClick={() => run(() => proposalApi.approveContinuity(orgId, opportunityId))}
                >
                  Run acceptance gate
                </Button>
              </div>
              {gatePassed && (
                <Badge tone="success">Acceptance gate passed. The proposal is handed off to Contract AI.</Badge>
              )}
            </div>
          )}
          {err && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{err}</p>}
        </div>
      </CardBody>
    </Card>
  );
}
